import { Box, Button, Chip, CircularProgress, LinearProgress, Typography } from '@mui/material'
import { MdClose } from 'react-icons/md'
import { CartItemType } from '../../utils/sharedTypes'
import { formatPrice } from './marketplaceAssets'

type CartBarPropsType = {
  items: CartItemType[]
  target: number
  submitting: boolean
  error: boolean
  /** Message du serveur à afficher à la place du message générique. */
  errorMessage?: string | null
  onRemove: (productCode: string) => void
  onContinue: () => void
  /** Formation financée entièrement par le financeur imposé (IPERIA) : pas de prix à afficher. */
  hidePrice?: boolean
}

const CartBar = ({
  items,
  target,
  submitting,
  error,
  errorMessage,
  onRemove,
  onContinue,
  hidePrice = false,
}: CartBarPropsType) => {
  if (items.length === 0) return null

  const total = hidePrice ? 0 : items.reduce((sum, item) => sum + (item.listingPrice ?? 0), 0)
  // L'objectif de 3 formations cadre la progression, sans jamais bloquer un ajout supplémentaire.
  const progress = Math.min((items.length / target) * 100, 100)
  const remaining = target - items.length

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {/* Pas d'ombre portée : la barre se détache par sa bordure et la jauge de progression. */}
      <LinearProgress variant="determinate" value={progress} sx={{ height: 4, borderRadius: 0 }} />
      {/* Box + gap plutôt qu'un Grid à `spacing` : ses marges négatives faisaient déborder la barre
          de 16 px sur mobile, poussant « Continuer » et les croix des puces hors de l'écran. */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: { xs: 1.5, md: 2 },
          maxWidth: 1200,
          mx: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, md: 2 },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            {`${items.length} sur ${target} formation${target > 1 ? 's' : ''}`}
            {remaining > 0 && (
              <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {` — encore ${remaining} pour compléter votre parcours`}
              </Typography>
            )}
          </Typography>
          {/* Sur téléphone, une seule ligne qui défile : la barre reste basse et chaque croix accessible. */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              flexWrap: { xs: 'nowrap', md: 'wrap' },
              overflowX: { xs: 'auto', md: 'visible' },
              pb: { xs: 0.5, md: 0 },
            }}
          >
            {items.map((item) => (
              <Chip
                key={item.productCode}
                label={item.themeTitle || item.productName}
                onDelete={() => onRemove(item.productCode)}
                deleteIcon={<MdClose />}
                disabled={submitting}
                sx={{ flexShrink: 0, maxWidth: { xs: 240, md: '100%' } }}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
          {total > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
              Total {formatPrice(total)}
            </Typography>
          )}
          <Button
            variant="contained"
            size="large"
            onClick={onContinue}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ flex: { xs: 1, md: 'none' }, minHeight: 44 }}
          >
            Continuer
          </Button>
        </Box>
        {error && (
          <Typography variant="body2" sx={{ color: 'error.main', textAlign: { xs: 'left', md: 'right' } }}>
            {errorMessage || "L'ajout a échoué, merci de réessayer."}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default CartBar
